import { Company } from '@/context/types';
import { BaseFilter, PaginationResponse } from '@/hooks/usePaginationFilter';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  CompanyDashboardData,
  getAllCompanies,
  getCompaniesCountByPlanId,
  getCompanyById,
  getCompanyDataForDashboard,
  onboardCompany,
  OnboardCompanyBody,
  updateCompanyAndOwner,
  UpdateCompanyAndOwnerResponse,
  UpdateCompanyDto,
  updateCompanyDeferFees,
} from '@/services';
import { CompaniesCount } from './organizationsSlice';

export type CompanyFilter = BaseFilter & {
  search?: string;
  organizationId?: string;
  numberOfOutlets?: CompaniesCount;
};

export type CompanyQuery = {
  id: string;
  filter?: CompanyFilter;
};

export type CompanyWithOrganization = Company & {
  organization: Partial<Company>;
};

export const companySlice = createApi({
  reducerPath: 'company',
  tagTypes: ['companies', 'company'],
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    getAllCompanies: builder.query<PaginationResponse<Company>, CompanyQuery>({
      queryFn: async ({ id, filter }) => {
        const data = await getAllCompanies({ id, filter });
        return { data };
      },
      providesTags: [{ type: 'companies' }],
    }),
    getDashboardData: builder.query<CompanyDashboardData, { id: string; organizationId: string }>({
      queryFn: async ({ id, organizationId }) => {
        const data = await getCompanyDataForDashboard(id, organizationId);
        return { data };
      },
      providesTags: [{ type: 'company' }],
    }),
    getCompanyById: builder.query<CompanyWithOrganization, { id: string; organizationId: string }>({
      queryFn: async ({ id, organizationId }) => {
        const data = await getCompanyById(id, organizationId);
        return { data };
      },
      providesTags: [{ type: 'company' }],
    }),
    updateCompanyAndOwner: builder.mutation<UpdateCompanyAndOwnerResponse, UpdateCompanyDto>({
      queryFn: async (updateCompanyDto) => {
        const data = await updateCompanyAndOwner(updateCompanyDto);
        return { data };
      },
      invalidatesTags: [{ type: 'companies' }, { type: 'company' }],
    }),
    onboardCompany: builder.mutation<Company, { body: OnboardCompanyBody; organizationId: string }>(
      {
        queryFn: async ({ body, organizationId }) => {
          const data = await onboardCompany(body, organizationId);
          return { data };
        },
      },
    ),
    getCompaniesCountByPlanId: builder.query<number, { organizationId: string; planId: string }>({
      queryFn: async ({ organizationId, planId }) => {
        const data = await getCompaniesCountByPlanId(organizationId, planId);
        return { data };
      },
    }),
    updateCompanyDeferFees: builder.mutation<Company, { companyId: string; deferFees: boolean }>({
      queryFn: async ({ companyId, deferFees }) => {
        const data = await updateCompanyDeferFees(companyId, deferFees);
        return { data };
      },
      invalidatesTags: [{ type: 'company' }, { type: 'companies' }],
    }),
  }),
});

export const {
  useGetAllCompaniesQuery,
  useGetDashboardDataQuery,
  useUpdateCompanyAndOwnerMutation,
  useOnboardCompanyMutation,
  useGetCompaniesCountByPlanIdQuery,
  useGetCompanyByIdQuery,
  useUpdateCompanyDeferFeesMutation,
} = companySlice;
